import ky, { type KyInstance, HTTPError } from 'ky';
import { env } from '@shared/config/env';
import { parseProblemDetail, ApiError } from './problem-detail';
import { tokenStorage, type AuthTokens } from './tokens';

export const ADMIN_PREFIX = '/api/admin/v1';

type RefreshHandler = () => Promise<AuthTokens | null>;
type UnauthorizedHandler = () => void;

let refreshHandler: RefreshHandler | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let inflightRefresh: Promise<AuthTokens | null> | null = null;

export function registerAuthHandlers(opts: {
  onRefresh: RefreshHandler;
  onUnauthorized: UnauthorizedHandler;
}): void {
  refreshHandler = opts.onRefresh;
  unauthorizedHandler = opts.onUnauthorized;
}

const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/password/reset/request',
  '/auth/password/reset/confirm',
  '/auth/invite/accept',
];

/**
 * Endpoints that must never carry a Bearer header — even a stale one would be noise
 * (and on some backends a hard 401). Login + refresh + invite-accept are anonymous;
 * logout takes the refresh token in the body.
 */
const ANONYMOUS_PATHS = NO_REFRESH_PATHS;

function isAuthEndpoint(url: string): boolean {
  return NO_REFRESH_PATHS.some((p) => url.includes(`${ADMIN_PREFIX}${p}`));
}

function isAnonymousEndpoint(url: string): boolean {
  return ANONYMOUS_PATHS.some((p) => url.includes(`${ADMIN_PREFIX}${p}`));
}

async function runRefresh(): Promise<AuthTokens | null> {
  if (!refreshHandler) return null;
  inflightRefresh ??= refreshHandler().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

export const http: KyInstance = ky.create({
  prefixUrl: `${env.apiBaseUrl}${ADMIN_PREFIX}`.replace(/\/+$/, ''),
  timeout: 30_000,
  retry: 0,
  hooks: {
    beforeRequest: [
      (request) => {
        // Anonymous auth endpoints (login, refresh, invite/accept, password reset)
        // must never carry a Bearer header. Strip any inherited one defensively.
        if (isAnonymousEndpoint(request.url)) {
          request.headers.delete('Authorization');
        } else {
          const token = tokenStorage.getAccess();
          if (token && !request.headers.has('Authorization')) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        }
        if (!request.headers.has('Accept-Language')) {
          const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en';
          request.headers.set('Accept-Language', lang.startsWith('ru') ? 'ru' : 'en');
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status !== 401 || isAuthEndpoint(request.url)) return response;

        const tokens = await runRefresh();
        if (!tokens) {
          unauthorizedHandler?.();
          return response;
        }
        const retried = new Request(request, { headers: new Headers(request.headers) });
        retried.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
        return ky(retried, { retry: 0 });
      },
    ],
    beforeError: [
      async (error) => {
        if (error instanceof HTTPError) {
          const apiError = await parseProblemDetail(error.response);
          return apiError as unknown as HTTPError;
        }
        return error;
      },
    ],
  },
});

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export { ApiError };
